import { checkpointVideoOperation } from 'js/src/data/video-data';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { VideoStateTool } from './VideoStateTool';
import type { RootState } from 'js/src/libs/RootState';
import { isVoiceDrivenSceneGroup } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import {
    deleteLuminaryOverlayOperation,
    deleteSceneGroupOperation,
} from 'js/src/luminary/luminary-operations';
import { isSceneAnOverlay } from 'js/src/luminary/luminary-selectors';

export const legacyDeleteSceneSchema = z.object({
    scene_index: z.number().describe('The scene index to delete'),
});
export type DeleteSceneArgs = z.infer<typeof legacyDeleteSceneSchema>;

export class LegacyDeleteSceneTool extends VideoStateTool<typeof legacyDeleteSceneSchema> {
    protected override readonly schema = legacyDeleteSceneSchema;
    protected override readonly toolName = TOOL_NAMES.deleteScene;
    public override readonly subagentAccess = SubagentAccess.UNAVAILABLE;
    protected override readonly toolDescription = 'Delete a scene from the video';

    public override getToolDisplayName(): string {
        return 'Deleting scene';
    }

    public get statusIcon(): string {
        return 'delete';
    }

    public async execute(args: DeleteSceneArgs, context: ToolExecutionContext) {
        const { scene_index } = args;
        const { parentActionGroup } = context;

        try {
            if (scene_index >= this.luminary.scenes.length) {
                return `Error: scene_index ${scene_index} is out of bounds. Video has ${this.luminary.scenes.length} scenes`;
            }

            const scene = this.luminary.scenes[scene_index];
            if (!scene) {
                return `Error: No scene found at index ${scene_index}`;
            }
            const sceneGroup = scene.sceneGroup;
            if (!sceneGroup) {
                return `Error: No scene group found for scene at index ${scene_index}`;
            }

            // The Lumen5 branding scene is required on the free plan and can only be
            // removed by users whose plan allows removing branding.
            if (scene.sceneCategory === VideoData.CATEGORY_BRAND) {
                const canRemoveBranding = SERVER?.USER?.capabilities?.no_branding ?? false;
                if (!canRemoveBranding) {
                    return `Error: This is the Lumen5 branding scene, which is required on the free plan and cannot be deleted. Use the ${TOOL_NAMES.suggestUpgrade} tool (feature "remove_branding") to let the user upgrade to remove Lumen5 branding.`;
                }
            }

            // Check if this is a voice-driven video (talking head or voiceover)
            if (isVoiceDrivenSceneGroup(sceneGroup)) {
                if (!isSceneAnOverlay(this.getState(), scene)) {
                    return `Error: Scene ${scene_index} is the base/presenter scene of a voice-driven scene group, which carries the voice-over and controls the group's timing, so it can't be deleted. You can delete a scene added to this scene group, or edit the transcript or replace the voice-over to change the base content.`;
                }

                try {
                    const { deleteLuminaryOverlay, checkpointVideo } = makeOperationsCallable(
                        { deleteLuminaryOverlayOperation, checkpointVideoOperation },
                        this.luminary,
                        this.dispatch,
                        this.getState as () => RootState,
                    );

                    deleteLuminaryOverlay(scene.id, sceneGroup.id, false, parentActionGroup);
                    checkpointVideo({ systemTriggered: false, parentActionGroup });

                    return `Success: Deleted scene at index ${scene_index}. The video now has ${this.luminary.scenes.length} scenes.`;
                } catch (error) {
                    const errorMessage =
                        error instanceof Error ? error.message : 'Unknown error occurred';
                    return `Error deleting scene: ${errorMessage}`;
                }
            }

            // currently only support deleting scene groups containing a single scene (shouldn't happen due to above
            // voicedriven check)
            if (sceneGroup.scenes.length !== 1) {
                return `Error: Cannot delete scene at index ${scene_index} because it contains multiple scenes.`;
            }

            const sceneGroupId = sceneGroup.id;

            const { deleteSceneGroup, checkpointVideo } = makeOperationsCallable(
                {
                    deleteSceneGroupOperation,
                    checkpointVideoOperation,
                },
                this.luminary,
                this.dispatch,
                () => null as unknown as RootState, // NOTE: no state currently
            );

            try {
                deleteSceneGroup(sceneGroupId, false, parentActionGroup);
                checkpointVideo({ systemTriggered: false, parentActionGroup });

                return `Success: Deleted scene at index ${scene_index}. The video now has ${this.luminary.scenes.length} scenes.`;
            } catch (error) {
                const errorMessage =
                    error instanceof Error ? error.message : 'Unknown error occurred';
                return `Error deleting scene: ${errorMessage}`;
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error deleting scene: ${errorMessage}`;
        }
    }
}
